// Public privacy policy page (no auth) — used for the App Store "Privacy Policy URL".
// Live at: <your-domain>/privacy

const EFFECTIVE_DATE = "August 3, 2026";
const COMPANY = "Land ClearCo, LLC";
const CONTACT_EMAIL = "privacy@landclearco.com";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading text-xl font-bold text-[#00341d] mb-3">{title}</h2>
    <div className="space-y-3 text-zinc-700 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="privacy-policy-page">
      <header className="bg-[#00341d] text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">Land ClearCo</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Privacy Policy</h1>
          <p className="text-emerald-100/80 mt-2 text-sm">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-zinc-700 leading-relaxed mb-8">
          This Privacy Policy explains how {COMPANY} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses,
          and protects information when you use the LandClearCo quote-management and field-execution
          application on web and iOS (the &ldquo;App&rdquo;). The App is an internal business tool for
          LandClearCo staff to create and manage customer quotes, run job-site operations, and
          collect payments.
        </p>

        <Section title="Information We Collect">
          <p>Depending on how the App is used, we may collect:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account &amp; access data:</strong> the admin password used to sign in.</li>
            <li><strong>Customer records:</strong> names, email addresses, phone numbers, mailing and property addresses, and publicly available parcel information (e.g., acreage, parcel ID) that you enter or look up.</li>
            <li><strong>Quote &amp; job data:</strong> quotes, proposals, line items, notes, uploaded quote PDFs, change orders, and job-costing logs.</li>
            <li><strong>Photos:</strong> job-site images you choose to attach (via camera or photo library).</li>
            <li><strong>Location data:</strong> your device&rsquo;s precise location while a job is active, including <strong>background location</strong>, used to display your position on the job map and to warn you when you approach a property boundary. Location is used only for these operational features.</li>
            <li><strong>Payment information:</strong> processed by our payment provider (Stripe). We do not store full card numbers on our servers.</li>
          </ul>
        </Section>

        <Section title="How We Use Information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To create, send, and manage customer quotes and proposals.</li>
            <li>To display maps, measure work areas, and provide live GPS tracking with boundary alerts during active jobs.</li>
            <li>To process payments and record job costs.</li>
            <li>To communicate with customers (e.g., emailing a quote when you choose to send it).</li>
            <li>To operate, secure, and improve the App.</li>
          </ul>
        </Section>

        <Section title="Location &amp; Background Use">
          <p>
            The App can access your location in the background <strong>only during an active job</strong> so it can
            keep you within approved work areas and alert you if you cross a property boundary, even
            when your screen is off. You can disable location permission at any time in your device
            settings; doing so will turn off the live-map and boundary-alert features.
          </p>
        </Section>

        <Section title="Notifications">
          <p>
            The App uses on-device (local) notifications to deliver boundary and safety alerts. These
            alerts are generated on your device and are not marketing messages.
          </p>
        </Section>

        <Section title="How Information Is Shared">
          <p>We do not sell your personal information. We share data only with service providers that help us operate the App:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Stripe</strong> — payment processing.</li>
            <li><strong>Mapbox</strong> — maps and geospatial rendering.</li>
            <li><strong>Resend</strong> — sending quote and follow-up emails.</li>
            <li><strong>Airtable</strong> — optional syncing of quote/customer records.</li>
            <li><strong>Public parcel data (e.g., NC OneMap)</strong> — to look up property information from an address.</li>
          </ul>
          <p>We may also disclose information if required by law or to protect our rights and safety.</p>
        </Section>

        <Section title="Data Retention &amp; Security">
          <p>
            We retain business records (quotes, customers, job data) for as long as needed to operate
            the business and meet legal or accounting obligations. We use reasonable administrative and
            technical safeguards to protect information; however, no method of transmission or storage
            is 100% secure.
          </p>
        </Section>

        <Section title="Children&rsquo;s Privacy">
          <p>The App is a business tool intended for adults and is not directed to children under 13.</p>
        </Section>

        <Section title="Your Choices">
          <p>
            You can control camera, photo, location, and notification permissions in your device
            settings. To request access to, correction of, or deletion of personal information we hold,
            contact us using the details below.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Material changes will be reflected by
            updating the effective date at the top of this page.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>
            If you have questions about this Privacy Policy, contact {COMPANY} at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00341d] font-semibold underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <p className="text-xs text-zinc-400 mt-10 border-t border-zinc-200 pt-6">
          &copy; {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </p>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
